import { useRouter } from 'next/navigation';
import { useCallback, useState } from 'react';
import { RecentlyReadContentDto } from '@diritto/common/recentlyRead/dtos/recently-read.dto';
import { ContentPublicRelayDto } from '@diritto/common/relay/dtos/content-relay.dtos';
import { EpisodeRelayDto } from '@diritto/common/relay/dtos/episode-relay.dtos';
import { PersonalContentRelayDto } from '@diritto/common/relay/dtos/personal-relay.dtos';
import { ProfileLitePublicRelayDto } from '@diritto/common/relay/dtos/profile-lite-relay.dtos';
import { Color } from '@diritto/common/style/color';
import { Typography } from '@diritto/common/style/typography';
import {
  ContentListType,
  TrackerExtraInfo,
  TrackerPosition,
  TrackingEventType,
} from '@diritto/common/tracker/tracking-event.constant';
import { ContentItemMeta } from 'app-components/contents/ContentItemMeta';
import { ButtonSize } from 'constants/button';
import { useTrackingState } from 'contexts/TrackingProvider';
import { NotiIcon } from 'icons/NotiIcon';
import { TimeIcon } from 'icons/TimeIcon';
import { useAddNewEpisodeNotiMutation } from 'queries/contentNoti/useAddNewEpisodeNotiMutation';
import { useDeleteNewEpisodeNotiMutation } from 'queries/contentNoti/useDeleteNewEpisodeNotiMutation';
import { useContentState } from 'state-hooks/contents/useContentState';
import { useReadContinueOrNext } from 'state-hooks/episode/useReadContinueOrNext';
import { Button } from 'ui-components/common/Button';
import { IconButton } from 'ui-components/common/IconButton';
import { Link } from 'ui-components/common/Link';
import { RelativeDateText } from 'ui-components/common/RelativeDateText';
import { ContentCover } from 'ui-components/contents/ContentCover';
import { showToast } from 'utils/modal';

interface FavoriteContentItemProps {
  personalContent: PersonalContentRelayDto;
  content: ContentPublicRelayDto;
  profile?: ProfileLitePublicRelayDto;
  listItemIndex?: number;
  recentlyReadContent?: RecentlyReadContentDto;
  episode?: EpisodeRelayDto;
}

const trackerExtraInfo: TrackerExtraInfo = {
  trackerPosition: TrackerPosition.My,
};

export const FavoriteContentItem = ({
  content,
  profile,
  recentlyReadContent,
  episode,
  listItemIndex = 0,
  personalContent,
}: FavoriteContentItemProps) => {
  const { tracker } = useTrackingState();
  const router = useRouter();
  const handleClick = useCallback(() => {
    tracker.track(TrackingEventType.SelectContentListItem, {
      listType: ContentListType.Favorites,
      listItemIndex,
      contentId: content.id,
      trackerExtraInfo,
    });
  }, [content.id, listItemIndex, tracker]);
  const { isUpdated } = useContentState(content);
  const readContinueOrNext = useReadContinueOrNext(content, episode, recentlyReadContent);
  const [isNoti, setIsNoti] = useState(personalContent.isNotiEnabled);

  const { mutate: addNewEpisodeNotiMutate } = useAddNewEpisodeNotiMutation(content.id, trackerExtraInfo, {
    onMutate: () => {
      setIsNoti(true);
    },
    onError: () => {
      setIsNoti(false);
    },
  });
  const { mutate: deleteNewEpisodeNotiMutate } = useDeleteNewEpisodeNotiMutation(content.id, trackerExtraInfo, {
    onMutate: () => {
      setIsNoti(false);
    },
    onError: () => {
      setIsNoti(true);
    },
  });

  const handlePressNoti = useCallback(() => {
    if (isNoti) {
      deleteNewEpisodeNotiMutate();
      showToast('회차 등록 알림이 해제되었어요.');
    } else {
      addNewEpisodeNotiMutate();
      showToast('회차 등록 알림이 설정되었어요.');
    }
  }, [addNewEpisodeNotiMutate, deleteNewEpisodeNotiMutate, isNoti]);

  const handleClickRead = useCallback(() => {
    if (readContinueOrNext?.readEpisodeId) {
      router.push(`/contents/${content.id}/episodes/${readContinueOrNext.readEpisodeId}`);
    }
  }, [readContinueOrNext?.readEpisodeId, router, content.id]);

  return (
    <div className="flex flex-row w-full flex-1">
      <div className="flex flex-row w-full items-center gap-4" onClick={handleClick}>
        <Link href={`/contents/${content.id}`}>
          <ContentCover size={90} content={content} />
        </Link>
        <div className="flex flex-col gap-1 flex-1">
          <ContentItemMeta
            content={content}
            profile={profile}
            href={`/contents/${content.id}`}
            showCategoryType
            showEpisodeCount
          />
          {content.latestEpisodeOpenedAt && (
            <div className="flex flex-row w-full items-center">
              {isUpdated && <p className="flex w-1.5 h-1.5 bg-primary-500 rounded-full mr-1 items-center" />}
              <TimeIcon />
              <RelativeDateText className="text-xxxs text-gray-700 ml-1" date={content.latestEpisodeOpenedAt}>
                &nbsp;업데이트
              </RelativeDateText>
            </div>
          )}
          {readContinueOrNext?.buttonText && (
            <Button
              className="!w-fit mt-1"
              label={readContinueOrNext.buttonText}
              size={ButtonSize.XSmall}
              buttonTypography={Typography.XXXSmall}
              onClick={handleClickRead}
            />
          )}
        </div>
        <IconButton className="ml-auto w-6 h-6" onClick={handlePressNoti}>
          <NotiIcon
            fill={isNoti}
            color={isNoti ? Color.Primary500 : Color.Gray600}
            fillColor={isNoti ? Color.Primary500 : Color.Gray600}
          />
        </IconButton>
      </div>
    </div>
  );
};
